import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import ComplianceShell from 'features/compliance/ComplianceShell'
import CompliancePage from 'features/compliance/CompliancePage'
import ProgrammePage from 'features/compliance/ProgrammePage'
import LegislationPage from 'features/compliance/legislation/LegislationPage'
import ObligationSetupPage from 'features/compliance/ObligationSetupPage'
import RemedialsSection from 'features/compliance/RemedialsSection'
import AssetRegisterPage from 'features/assets/AssetRegisterPage'
import PermitsPage from 'features/permits/PermitsPage'

const ComplianceWorkspace = ({ bpId, initialSection = 'overview' }) => {
  const [section, setSection] = useState(initialSection)
  const [, setSearchParams] = useSearchParams()
  const frameRef = useRef(null)
  const shownRef = useRef(initialSection)

  useEffect(() => { setSection(initialSection) }, [initialSection])

  useEffect(() => {
    if (shownRef.current === section) return
    shownRef.current = section
    const node = frameRef.current
    if (!node) return
    const strip = document.querySelector('[data-tour="passport-tabs"]')
    const barBottom = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--passport-bar-bottom')) || 0
    const limit = strip ? Math.max(barBottom, strip.getBoundingClientRect().bottom) : barBottom
    const top = node.getBoundingClientRect().top
    if (top < limit) window.scrollTo({ top: Math.max(0, window.scrollY + top - limit - 8), behavior: 'instant' })
  }, [section])

  const changeSection = useCallback(key => {
    setSection(key)
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (key === 'overview') next.delete('section')
      else next.set('section', key)
      return next
    }, { replace: true })
  }, [setSearchParams])

  const shared = { bpId, embedded: true, chromeless: true, onTabChange: changeSection }

  let content
  switch (section) {
    case 'programme':
      content = <ProgrammePage {...shared} />
      break
    case 'legislation':
      content = <LegislationPage {...shared} />
      break
    case 'remedials':
      content = <RemedialsSection {...shared} />
      break
    case 'setup':
      content = <ObligationSetupPage {...shared} />
      break
    case 'assets':
      content = <AssetRegisterPage {...shared} />
      break
    case 'permits':
      content = <PermitsPage {...shared} />
      break
    default:
      content = <CompliancePage buildingPassportId={bpId} embedded chromeless onTabChange={changeSection} />
  }

  return (
    <Frame ref={frameRef}>
      <ComplianceShell activeTab={section} bpId={bpId} embedded onTabChange={changeSection}>
        <Body>{content}</Body>
      </ComplianceShell>
    </Frame>
  )
}

export default ComplianceWorkspace

const Frame = styled.div`
  min-width: 0;
`

const Body = styled.div`
  min-height: 70vh;
`
